import React, { useEffect, useRef, useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { canManageTemplates, canSeeBillingAmounts, canViewBilling } from './roles';

// Close a popover on a click outside `ref` or on Escape.
const useDismiss = (open, ref, setOpen) => {
  useEffect(() => {
    if (!open) return undefined;
    const onMouseDown = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onKeyDown = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onMouseDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open, ref, setOpen]);
};

const Navigation = ({ user, onLogout, onChangePassword }) => {
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [linksOpen, setLinksOpen] = useState(false);
  const menuRef = useRef(null);
  const linksRef = useRef(null);

  useDismiss(menuOpen, menuRef, setMenuOpen);
  useDismiss(linksOpen, linksRef, setLinksOpen);

  const handleLogout = () => {
    onLogout();
    navigate('/login');
  };

  const links = [
    { to: '/home', label: 'Home' },
    { to: '/forms', label: 'Forms' },
    canManageTemplates(user?.role) && { to: '/templates', label: 'Templates' },
    { to: '/team', label: 'Team' },
    canViewBilling(user?.role) && {
      to: '/billing',
      label: canSeeBillingAmounts(user.role) ? 'Billing' : 'Plan',
    },
  ].filter(Boolean);

  const linkClass = (size) => ({ isActive }) =>
    `${size} rounded-md font-medium transition-colors duration-200 ${
      isActive
        ? 'text-purple-400 bg-purple-500/10'
        : 'text-gray-300 hover:text-purple-400 hover:bg-purple-500/10'
    }`;
  const navLinkClass = linkClass('px-3 py-2 text-sm');
  // Taller rows in the phone menu, sized for a thumb.
  const menuLinkClass = linkClass('px-3 py-3 text-base');

  return (
    <nav className="bg-gray-900/95 backdrop-blur-sm border-b border-purple-500/20 sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Below md the links don't fit beside the account controls, so
              they fold into a menu that drops down over the page. */}
          <div ref={linksRef} className="md:hidden">
            <button
              onClick={() => setLinksOpen((open) => !open)}
              aria-label="Menu"
              aria-expanded={linksOpen}
              className="flex items-center bg-transparent hover:bg-purple-500/10 text-gray-300 hover:text-white p-2 rounded-md transition-colors duration-200"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="w-6 h-6"
                viewBox="0 0 20 20"
                fill="currentColor"
                aria-hidden="true"
              >
                {linksOpen ? (
                  <path d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z" />
                ) : (
                  <path
                    fillRule="evenodd"
                    d="M2 4.75A.75.75 0 012.75 4h14.5a.75.75 0 010 1.5H2.75A.75.75 0 012 4.75zM2 10a.75.75 0 01.75-.75h14.5a.75.75 0 010 1.5H2.75A.75.75 0 012 10zm0 5.25a.75.75 0 01.75-.75h14.5a.75.75 0 010 1.5H2.75a.75.75 0 01-.75-.75z"
                    clipRule="evenodd"
                  />
                )}
              </svg>
            </button>
            {linksOpen && (
              <div className="absolute inset-x-0 top-full flex flex-col gap-1 bg-gray-900 border-b border-purple-500/20 shadow-2xl px-4 sm:px-6 py-3">
                {links.map(({ to, label }) => (
                  <NavLink
                    key={to}
                    to={to}
                    onClick={() => setLinksOpen(false)}
                    className={menuLinkClass}
                  >
                    {label}
                  </NavLink>
                ))}
              </div>
            )}
          </div>
          <div className="hidden md:flex items-center space-x-8">
            {links.map(({ to, label }) => (
              <NavLink key={to} to={to} className={navLinkClass}>
                {label}
              </NavLink>
            ))}
          </div>
          <div className="flex items-center space-x-4">
            <div ref={menuRef} className="relative">
              <button
                onClick={() => setMenuOpen((open) => !open)}
                aria-haspopup="menu"
                aria-expanded={menuOpen}
                className="flex items-center gap-1.5 bg-transparent hover:bg-purple-500/10 text-gray-400 hover:text-white text-sm px-2 py-2 rounded-md transition-colors duration-200"
              >
                {/* On phones an icon stands in for the address, which is too
                    wide for the bar and heads the menu instead. */}
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="w-5 h-5 md:hidden"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                  aria-hidden="true"
                >
                  <path
                    fillRule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-5.5-2.5a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0zM10 12a5.99 5.99 0 00-4.793 2.39A6.483 6.483 0 0010 16.5a6.483 6.483 0 004.793-2.11A5.99 5.99 0 0010 12z"
                    clipRule="evenodd"
                  />
                </svg>
                <span className="sr-only md:not-sr-only">{user?.email}</span>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className={`w-4 h-4 transition-transform duration-200 ${menuOpen ? 'rotate-180' : ''}`}
                  viewBox="0 0 20 20"
                  fill="currentColor"
                  aria-hidden="true"
                >
                  <path
                    fillRule="evenodd"
                    d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>
              {menuOpen && (
                <div
                  role="menu"
                  className="absolute right-0 top-full mt-2 w-48 bg-gray-800 border border-purple-500/20 rounded-lg shadow-2xl py-1 z-50"
                >
                  {/* The address, for phones, where the bar shows an icon.
                      Screen readers already hear it as the button's name. */}
                  <p
                    aria-hidden="true"
                    className="md:hidden px-4 pt-1.5 pb-2.5 mb-1 text-xs text-gray-400 break-words border-b border-purple-500/20"
                  >
                    {user?.email}
                  </p>
                  <button
                    role="menuitem"
                    onClick={() => {
                      setMenuOpen(false);
                      onChangePassword();
                    }}
                    className="w-full text-left px-4 py-2.5 text-sm bg-transparent text-gray-300 hover:bg-purple-500/10 hover:text-purple-400 transition-colors duration-150"
                  >
                    Change Password
                  </button>
                </div>
              )}
            </div>
            <button
              onClick={handleLogout}
              className="bg-red-500/10 hover:bg-red-500/20 text-red-400 hover:text-red-300 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 border border-red-500/20 hover:border-red-500/40">
              Logout
            </button>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navigation;
